import React, { useState, useEffect, useContext } from 'react';
import { Table } from "reactstrap";
import RendicionViaticosService from "../axios_services/rendicion_viaticos.service";
import Loader from "../components/Loader/Loader";
import { ButtonUpdate, ButtonDelete, ButtonInsert, ButtonSearch } from '../components/Buttons/Buttons';
import ModalRendicionViatico from '../components/Modal/ModalRendicionViatico';
import { FileText, CheckCircle, XCircle, Paperclip, Lock } from 'lucide-react';
import { AppContext } from '../application/provider';
import ArchivosService from '../axios_services/archivos.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './CajaFinancieroMovimiento.css';
import ModalDetalleGastoRendicion from '../components/Modal/ModalDetalleGastoRendicion';
import ModalArchivosAdjuntos from '../components/Modal/ModalArchivosAdjuntos';

function ModalValidacion({ rendicion, onConfirm, onClose }) {
    if (!rendicion) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
            <div style={{
                background: '#fff', borderRadius: 14, padding: '32px 28px',
                minWidth: 340, maxWidth: 400, width: '90%',
                boxShadow: '0 8px 40px #0003',
                textAlign: 'center'
            }}>
                <p style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                    ¿Qué acción desea realizar sobre la rendición de
                </p>
                <p style={{ fontWeight: 700, fontSize: 15, color: '#14233a', marginBottom: 24 }}>
                    {rendicion.nombre_empleado}?
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    <button
                        onClick={() => onConfirm('aprobado')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '10px 22px', borderRadius: 8, border: 'none',
                            background: '#EAF3DE', color: '#3B6D11',
                            fontWeight: 600, fontSize: 13, cursor: 'pointer'
                        }}
                    >
                        <CheckCircle size={17} /> Validar
                    </button>
                    <button
                        onClick={() => onConfirm('rechazado')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '10px 22px', borderRadius: 8, border: 'none',
                            background: '#FCEBEB', color: '#A32D2D',
                            fontWeight: 600, fontSize: 13, cursor: 'pointer'
                        }}
                    >
                        <XCircle size={17} /> Rechazar
                    </button>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '10px 18px', borderRadius: 8,
                            border: '1px solid #ddd', background: '#f5f5f5',
                            color: '#555', fontWeight: 500, fontSize: 13, cursor: 'pointer'
                        }}
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
}

function RendicionViaticos() {
    const [rendiciones, setRendiciones] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRendicion, setSelectedRendicion] = useState(null);
    const [state] = useContext(AppContext);
    const [validaciones, setValidaciones] = useState({});
    const [rendicionAValidar, setRendicionAValidar] = useState(null);
    const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
    const [modalGastoDetalleOpen, setModalGastoDetalleOpen] = useState(false);
    const [archivosModalOpen, setArchivosModalOpen] = useState(false);
    const [archivosRendicion, setArchivosRendicion] = useState(null);

    const now = new Date();
    const mesActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const [periodoInput, setPeriodoInput] = useState(mesActual);
    const [periodoFiltro, setPeriodoFiltro] = useState(mesActual);

    const formatoSoles = (monto) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
        }).format(monto || 0);
    };

    const toTitleCase = (str) => {
        if (!str) return '';
        return str.toLowerCase().replace(/(?:^|\s)\w/g, match => match.toUpperCase());
    };

    const loadImg = (url) => new Promise(async (res) => {
        if (!url) return res(null);
        try {
            const r = await fetch(url);
            const b = await r.blob();
            const rd = new FileReader();
            rd.onloadend = () => res(rd.result);
            rd.onerror = () => res(null);
            rd.readAsDataURL(b);
        } catch {
            res(null);
        }
    });

    useEffect(() => {
        fetchRendiciones();
    }, []);

    const handleSearch = async () => {
        setPeriodoFiltro(periodoInput);
        await fetchRendiciones();
    };

    const fetchRendiciones = async () => {
        setIsLoading(true);
        try {
            const data = await RendicionViaticosService.getRendiciones();
            setRendiciones(data || []);
        } catch (error) {
            console.error('Error al obtener rendiciones:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNuevaRendicion = () => {
        setSelectedRendicion(null);
        setIsModalOpen(true);
    };

    const handleEditarRendicion = (rendicion) => {
        setSelectedRendicion(rendicion);
        setIsModalOpen(true);
    };

    const handleViewGastos = (rendicion) => {
        setGastoSeleccionado(rendicion);
        setModalGastoDetalleOpen(true);
    };

    const handleVerAdjuntos = (rendicion) => {
        setArchivosRendicion(rendicion);
        setArchivosModalOpen(true);
    };

    const handleEliminarRendicion = async (rendicionId) => {
        if (!window.confirm('¿Está seguro que desea eliminar este registro?')) return;
        try {
            setIsLoading(true);
            await RendicionViaticosService.deleteRendicion(rendicionId);
            await fetchRendiciones();
        } catch (error) {
            console.error('Error al eliminar rendicion:', error);
            alert('Error al eliminar el registro');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRendicion(null);
    };

    const handleSaveRendicion = async (rendicionData, archivosSeleccionados = []) => {
        try {
            setIsLoading(true);
            setIsModalOpen(false);
            
            let idRendicion = null;
            if (selectedRendicion) {
                idRendicion = selectedRendicion.rendicion_id;
                await RendicionViaticosService.updateRendicion(idRendicion, rendicionData);
            } else {
                const res = await RendicionViaticosService.insertRendicion(rendicionData);
                idRendicion = res.rendicion_id || (res.data && res.data.rendicion_id);
            }

            if (archivosSeleccionados && archivosSeleccionados.length > 0 && idRendicion) {
                const formData = new FormData();
                archivosSeleccionados.forEach(file => {
                    formData.append('archivos[]', file);
                });
                formData.append('usuario_id', state.user?.usuario_id || '');
                formData.append('origen_id', idRendicion);
                
                await RendicionViaticosService.subirArchivosAdjuntos(idRendicion, formData);
            }

            await fetchRendiciones();
        } catch (error) {
            console.error('Error al guardar rendicion:', error);
            alert(error.response?.data?.error || 'Ocurrió un error al guardar.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmarValidacion = async (estado) => {
        if (rendicionAValidar) {
            try {
                setIsLoading(true);
                await RendicionViaticosService.validarRendicion(rendicionAValidar.rendicion_id, { estado });
                await fetchRendiciones();
            } catch (error) {
                console.error('Error al validar rendicion:', error);
                alert('Ocurrió un error al validar.');
            } finally {
                setIsLoading(false);
                setRendicionAValidar(null);
            }
        }
    };

    const handleVerRendicionPDF = async (rend) => {
        setIsLoading(true);
        try {
            const [firmaColabRes, firmaTesRes] = await Promise.all([
                rend.usuario_id ? ArchivosService.getFirmaDigitalConvenio(rend.usuario_id).catch(() => null) : null,
                rend.tesoreria_id ? ArchivosService.getFirmaDigitalConvenio(rend.tesoreria_id).catch(() => null) : null,
            ]);

            const doc = new jsPDF('p', 'mm', 'a4');

            doc.setDrawColor(29, 52, 97);
            doc.setLineWidth(0.8);
            doc.line(15, 15, 195, 15);

            try {
                const l = await loadImg(`${window.location.origin}/logo_mec.png`);
                if (l) doc.addImage(l, undefined, 15, 18, 18, 18);
            } catch { }

            doc.setFontSize(16);
            doc.setTextColor(29, 52, 97);
            doc.setFont("helvetica", "bold");
            doc.text(`RENDICIÓN DE VIÁTICOS N° ${rend.numero_rendicion || rend.numero_planilla || ''}`, 45, 30);

            doc.setDrawColor(221, 227, 240);
            doc.setLineWidth(0.2);
            doc.line(15, 40, 195, 40);

            doc.setFontSize(10);

            let startY = 50;

            doc.setTextColor(29, 52, 97);
            doc.setFont("helvetica", "bold");
            doc.text("COLABORADOR:", 20, startY);
            doc.setTextColor(85, 85, 85);
            doc.setFont("helvetica", "normal");
            doc.text(toTitleCase(rend.nombre_empleado || ''), 60, startY);

            startY += 8;
            doc.setTextColor(29, 52, 97);
            doc.setFont("helvetica", "bold");
            doc.text("LUGAR:", 20, startY);
            doc.setTextColor(85, 85, 85);
            doc.setFont("helvetica", "normal");
            doc.text(toTitleCase(rend.detalle_ciudad || ''), 60, startY);

            startY += 8;
            doc.setTextColor(29, 52, 97);
            doc.setFont("helvetica", "bold");
            doc.text("PERÍODO:", 20, startY);
            doc.setTextColor(85, 85, 85);
            doc.setFont("helvetica", "normal");
            doc.text(`${rend.dia_inicio || ''} al ${rend.dia_fin || ''}`, 60, startY);

            startY += 8;
            doc.setTextColor(29, 52, 97);
            doc.setFont("helvetica", "bold");
            doc.text("FECHA DE REGISTRO:", 20, startY);
            doc.setTextColor(85, 85, 85);
            doc.setFont("helvetica", "normal");
            doc.text(rend.fecha_registro || '', 60, startY);

            startY += 10;
            doc.setTextColor(85, 85, 85);
            doc.setFont("helvetica", "normal");
            const textLines = doc.splitTextToSize(
                `Por medio del presente documento, el colaborador ${toTitleCase(rend.nombre_empleado)} realiza la rendición de los viáticos recibidos correspondientes a la comisión de servicio realizada en la ciudad de ${toTitleCase(rend.detalle_ciudad)}, desde el ${rend.dia_inicio} hasta el ${rend.dia_fin}. A continuación se detallan los montos recibidos, rendidos y la devolución correspondiente:`,
                170
            );
            doc.text(textLines, 20, startY);

            startY += (textLines.length * 5) + 5;

            const devolucion = (Number(rend.monto_recibido) || 0) - (Number(rend.monto_rendido) || 0);

            autoTable(doc, {
                startY,
                head: [['CONCEPTO', 'MONTO']],
                body: [
                    ['Monto Recibido (Planilla de Viáticos)', formatoSoles(rend.monto_recibido)],
                    ['Monto Rendido (Gastos Sustentados)', formatoSoles(rend.monto_rendido)],
                    [
                        { content: 'Devolución / Saldo', styles: { fontStyle: 'bold', textColor: [220, 38, 38] } },
                        { content: formatoSoles(rend.monto_devolucion ?? devolucion), styles: { fontStyle: 'bold', textColor: [220, 38, 38], halign: 'right' } }
                    ],
                ],
                theme: 'grid',
                styles: { fontSize: 10, cellPadding: 3, textColor: [85, 85, 85], lineColor: [221, 227, 240] },
                headStyles: { fillColor: [240, 244, 255], textColor: [29, 52, 97], fontStyle: 'bold', halign: 'center' },
                columnStyles: {
                    0: { halign: 'left' },
                    1: { halign: 'right', cellWidth: 50 },
                },
                margin: { left: 20, right: 20 },
            });

            startY = doc.lastAutoTable.finalY + 10;

            doc.setFontSize(10);
            doc.setTextColor(29, 52, 97);
            doc.setFont("helvetica", "bold");
            const notaLines = doc.splitTextToSize(
                "NOTA: El colaborador declara que los gastos consignados en esta rendición son verídicos y se encuentran debidamente sustentados con los comprobantes correspondientes. El saldo a devolver deberá ser reintegrado a Tesorería en un plazo no mayor a 03 días hábiles.",
                170
            );
            doc.text(notaLines, 20, startY);

            startY = doc.lastAutoTable.finalY + 50;

            const drawSignature = (imgData, label, subLabel, x) => {
                if (imgData) {
                    try { doc.addImage(imgData, undefined, x - 20, startY - 22, 40, 18); } catch { }
                }
                doc.setDrawColor(0);
                doc.setLineWidth(0.5);
                doc.line(x - 30, startY, x + 30, startY);
                doc.setFontSize(10);
                doc.setTextColor(29, 52, 97);
                doc.setFont('helvetica', 'bold');
                doc.text(label || '', x, startY + 5, { align: 'center' });
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(85, 85, 85);
                doc.text(subLabel, x, startY + 10, { align: 'center' });
            };

            const pageWidth = doc.internal.pageSize.width;
            drawSignature(firmaColabRes?.data?.base64 || firmaColabRes?.base64, toTitleCase(rend.nombre_empleado), "Colaborador", pageWidth * 0.30);
            drawSignature(firmaTesRes?.data?.base64 || firmaTesRes?.base64, toTitleCase(rend.nombre_tesoreria || "Responsable de Tesorería"), "Responsable de Tesorería", pageWidth * 0.70);

            window.open(URL.createObjectURL(doc.output("blob")));
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Error al generar el documento");
        } finally {
            setIsLoading(false);
        }
    };


    const rendicionesFiltradas = rendiciones.filter(r => r.dia_fin && r.dia_fin.startsWith(periodoFiltro));

    const badgeEstado = (rend) => {
        const est = Number(rend.validacion_id);
        if (est === 4) return (
            <span style={{ fontSize: 11, fontWeight: 500, color: '#3B6D11' }}>
                VALIDADO POR TESORERIA
            </span>
        );
        if (est === 2) return (
            <span style={{ fontSize: 11, fontWeight: 500, color: '#A32D2D' }}>
                OBSERVADO
            </span>
        );
        return (
            <span style={{ fontSize: 11, fontWeight: 500, color: '#555' }}>
                PENDIENTE
            </span>
        );
    };

    return (
        <div className="caja-flujo-container">
            {isLoading && <Loader />}

            <ModalValidacion
                rendicion={rendicionAValidar}
                onConfirm={handleConfirmarValidacion}
                onClose={() => setRendicionAValidar(null)}
            />

            <div className="flex items-center justify-between mb-4">
                <h1>RENDICIÓN DE VIÁTICOS</h1>
            </div>

            <div className="filtros" style={{ marginBottom: '10px' }}>
                <div>
                    <label>Mes:</label>
                    <input type="month" value={periodoInput} onChange={e => setPeriodoInput(e.target.value)} />
                </div>
            </div>

            <div className="mb-4">
                <ButtonSearch onClick={handleSearch} isLoading={isLoading} />
            </div>

            <div className="nuevo-movimiento-section mb-3">
                <ButtonInsert onClick={handleNuevaRendicion} />
            </div>

            <div className="table-container shadow-sm overflow-x-auto">
                <Table bordered hover size="sm" className="text-xs m-0">
                    <thead className="bg-[#14233a] text-white text-center align-middle">
                        <tr>
                            <th style={{ minWidth: '180px' }}>COLABORADOR</th>
                            <th style={{ minWidth: '150px' }}>ASUNTO</th>
                            <th style={{ minWidth: '120px' }}>FECHA RENDICIÓN</th>
                            <th>LUGAR</th>
                            <th style={{ minWidth: '110px' }}>FECHAS DE VIÁTICOS</th>
                            <th style={{ minWidth: '110px' }}>MONTO RECIBIDO</th>
                            <th style={{ minWidth: '110px' }}>MONTO RENDIDO</th>
                            <th style={{ minWidth: '120px' }}>SALDO DEVOLUCIÓN</th>
                            <th style={{ minWidth: '110px' }}>DETALLE GASTOS</th>
                            <th style={{ minWidth: '130px' }}>ARCHIVOS ADJUNTOS</th>
                            <th style={{ minWidth: '140px' }}>ESTADO RENDICIÓN</th>
                            <th style={{ minWidth: '130px' }}>DOCUMENTO FIRMADO</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody className="align-middle">
                        {rendicionesFiltradas.length > 0 ? (
                            rendicionesFiltradas.map((rend) => (
                                <tr key={rend.rendicion_id} style={{ backgroundColor: Number(rend.validacion_id) === 4 ? '#dcfce7' : (Number(rend.validacion_id) === 2 ? '#fef2f2' : '') }}>
                                    <td className="p-2 font-bold text-uppercase">{rend.nombre_empleado}</td>
                                    <td className="p-2 text-center text-xs">{rend.asunto ? rend.asunto.toUpperCase() : (rend.lugar ? rend.lugar.toUpperCase() : `PLANILLA ${rend.numero_planilla}`)}</td>
                                    <td className="p-2 text-center" style={{ fontSize: '10px' }}>{rend.fecha_registro}</td>
                                    <td className="p-2 text-center">{rend.detalle_ciudad}</td>
                                    <td className="p-2 text-center" style={{ fontSize: '10px' }}>{rend.dia_inicio} al {rend.dia_fin}</td>
                                    <td className="p-2 text-center text-primary" style={{ fontWeight: 'normal' }}>
                                        {formatoSoles(rend.monto_recibido)}
                                    </td>
                                    <td className="p-2 text-center text-success" style={{ fontWeight: 'normal' }}>
                                        {formatoSoles(rend.monto_rendido)}
                                    </td>
                                    <td className="p-2 text-center" style={{ fontWeight: 'normal', color: parseFloat(rend.monto_devolucion) < 0 ? '#ef4444' : 'inherit' }}>
                                        {formatoSoles(rend.monto_devolucion)}
                                    </td>
                                    <td className="p-2 text-center align-middle">
                                        <FileText
                                            color={rend.detalles && rend.detalles.length > 0 ? "#10b981" : "#ef4444"}
                                            size={20}
                                            style={{ cursor: rend.detalles && rend.detalles.length > 0 ? 'pointer' : 'default', margin: '0 auto' }}
                                            onClick={() => rend.detalles && rend.detalles.length > 0 && handleViewGastos(rend)}
                                        />
                                    </td>
                                    <td className="p-2 text-center align-middle">
                                        <div className="flex justify-center items-center">
                                            <button className="boton-accion" title="Ver Comprobantes" onClick={() => handleVerAdjuntos(rend)}>
                                                <Paperclip 
                                                    size={20} 
                                                    style={{ margin: '0 auto', color: rend.tiene_adjuntos ? "#10b981" : "#ef4444" }} 
                                                />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="p-2 text-center align-middle">
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {badgeEstado(rend)}
                                        </div>
                                    </td>
                                    <td className="p-2 text-center align-middle">
                                        {Number(rend.validacion_id) === 4 ? (
                                            <FileText
                                                color="#10b981"
                                                size={20}
                                                style={{ cursor: 'pointer', margin: '0 auto' }}
                                                onClick={() => handleVerRendicionPDF(rend)}
                                            />
                                        ) : (
                                            <FileText
                                                color="#ef4444"
                                                size={20}
                                                style={{ cursor: 'not-allowed', margin: '0 auto' }}
                                                title="Requiere validación por tesorería"
                                            />
                                        )}
                                    </td>
                                    <td className="p-2 text-center align-middle">
                                        <div className="flex justify-center items-center gap-2">
                                            {Number(rend.validacion_id) === 4 ? (
                                                <span className="text-2xl" title="Bloqueado" style={{ cursor: 'not-allowed', display: 'flex', alignItems: 'center' }}>
                                                    🔒
                                                </span>
                                            ) : (
                                                <>
                                                    {([8].includes(Number(state.user?.perfil_id)) || Number(rend.usuario_id) === Number(state.user?.usuario_id)) && (
                                                        <>
                                                            <ButtonUpdate onClick={() => handleEditarRendicion(rend)} />
                                                            <ButtonDelete onClick={() => handleEliminarRendicion(rend.rendicion_id)} />
                                                        </>
                                                    )}
                                                    {[8, 26].includes(Number(state.user?.perfil_id)) && (
                                                        <button
                                                            className="boton-accion text-xs font-bold"
                                                            title="Validar / Rechazar"
                                                            onClick={() => setRendicionAValidar(rend)}
                                                            style={{
                                                                padding: '4px 10px',
                                                                border: '1px solid #10b981', 
                                                                borderRadius: 4,
                                                                background: '#10b981', 
                                                                cursor: 'pointer', 
                                                                color: '#fff',
                                                                fontSize: '11px'
                                                            }}
                                                        >
                                                            Validar
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center text-gray-500 py-4">
                                    No hay datos disponibles.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalRendicionViatico
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveRendicion}
                rendicion={selectedRendicion}
                periodoFiltro={periodoFiltro}
            />

            <ModalDetalleGastoRendicion 
                isOpen={modalGastoDetalleOpen}
                onClose={() => setModalGastoDetalleOpen(false)}
                rendicion={gastoSeleccionado}
            />

            <ModalArchivosAdjuntos
                isOpen={archivosModalOpen}
                onClose={() => setArchivosModalOpen(false)}
                rendicion={archivosRendicion}
                onArchivoEliminado={() => fetchRendiciones()}
            />
        </div>
    );
}

export default RendicionViaticos;